import { Fragment, jsx, jsxs } from "react/jsx-runtime";
import { createElement, useEffect, useId, useMemo, useRef, useState } from "react";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import FormControlLabel from "@mui/material/FormControlLabel";
import classnames from "classnames";
import { useDebounce } from "../../hooks/index.js";
import { Box } from "../../Box/index.js";
import { Icon } from "../../Icon/index.js";
import { Checkbox } from "../../Checkbox/index.js";
import { CircularProgress } from "../../CircularProgress/index.js";
import { Tooltip } from "../../Tooltip/index.js";
import { Typography } from "../../Typography/index.js";
import { Badge, Label, Preloader } from "../common/index.js";
import { useAutocompleteReducer } from "../hooks/index.js";
import { ListBox } from "./ListBox.js";
import MultipleAutocompleteInput_styles from "./MultipleAutocompleteInput.styles.js";
function asyncGeneratorStep(gen, resolve, reject, _next, _throw, key, arg) {
    try {
        var info = gen[key](arg);
        var value = info.value;
    } catch (error) {
        reject(error);
        return;
    }
    if (info.done) resolve(value);
    else Promise.resolve(value).then(_next, _throw);
}
function _async_to_generator(fn) {
    return function() {
        var self = this, args = arguments;
        return new Promise(function(resolve, reject) {
            var gen = fn.apply(self, args);
            function _next(value) {
                asyncGeneratorStep(gen, resolve, reject, _next, _throw, "next", value);
            }
            function _throw(err) {
                asyncGeneratorStep(gen, resolve, reject, _next, _throw, "throw", err);
            }
            _next(void 0);
        });
    };
}
function _define_property(obj, key, value) {
    if (key in obj) Object.defineProperty(obj, key, {
        value: value,
        enumerable: true,
        configurable: true,
        writable: true
    });
    else obj[key] = value;
    return obj;
}
function _object_spread(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = null != arguments[i] ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if ("function" == typeof Object.getOwnPropertySymbols) ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
            return Object.getOwnPropertyDescriptor(source, sym).enumerable;
        }));
        ownKeys.forEach(function(key) {
            _define_property(target, key, source[key]);
        });
    }
    return target;
}
function MultipleAutocompleteInput_ownKeys(object, enumerableOnly) {
    var keys = Object.keys(object);
    if (Object.getOwnPropertySymbols) {
        var symbols = Object.getOwnPropertySymbols(object);
        if (enumerableOnly) symbols = symbols.filter(function(sym) {
            return Object.getOwnPropertyDescriptor(object, sym).enumerable;
        });
        keys.push.apply(keys, symbols);
    }
    return keys;
}
function _object_spread_props(target, source) {
    source = null != source ? source : {};
    if (Object.getOwnPropertyDescriptors) Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    else MultipleAutocompleteInput_ownKeys(Object(source)).forEach(function(key) {
        Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
    });
    return target;
}
const DEBOUNCE_TIMEOUT = 200;
const getSelectedLoadValues = (items, value)=>items.filter((item)=>null == value ? void 0 : value.includes(item.value)).map((item)=>`${item.prefix ? `${item.prefix}, ` : ''}${item.label}`).join(', ') || '';
const MultipleAutocompleteInput = (props)=>{
    const { id, value: initialValue, onChange, onBlur, onLogAction, label, labelIcon, tooltip, name, placeholder = '', required = false, limit = 50, disabledTooltip = 'Недоступен для выбранного типа заявки', disabled = false, clearable = true, loading = false, error = false, helperText = '', minInputValueLength = 1, renderTags, testId = 'test-id-multiple-autocomplete-input', additionalFilters, withoutMargin = false, renderOption } = props;
    const stringifiedValue = JSON.stringify(initialValue ? [
        ...initialValue
    ].sort() : void 0);
    const value = useMemo(()=>initialValue, [
        stringifiedValue
    ]);
    const { classes } = MultipleAutocompleteInput_styles();
    const reactId = useId();
    const inputId = useMemo(()=>null != id ? id : reactId, [
        id,
        reactId
    ]);
    const { items, status, hasMore, requestItems } = useAutocompleteReducer('load' === props.type ? props.loadItems : void 0);
    const [inputValue, setInputValue] = useState('');
    const [selectedValues, setSelectedValues] = useState(getSelectedLoadValues('local' === props.type ? props.externalItems : items, value));
    const [allSelectedItems, setAllSelectedItems] = useState('local' === props.type ? props.externalItems.filter((item)=>null == value ? void 0 : value.includes(item.value)) : []);
    const [isFirstRenderDone, setIsFirstRenderDone] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [filter, setFilter] = useState({
        limit,
        offset: 0,
        query: ''
    });
    const [open, setOpen] = useState(false);
    const offsetRef = useRef(0);
    const isInitialLoadRef = useRef(false);
    const debouncedValue = useDebounce(inputValue, DEBOUNCE_TIMEOUT);
    const inputValueForUse = inputValue.length < minInputValueLength ? inputValue : debouncedValue;
    const isThereSomeGroup = items.some((option)=>option.group);
    const isItemsLoading = isLoading || 'loading' === status;
    const autocompleteGetOptions = ()=>{
        if ('local' === props.type) return props.externalItems;
        if (!offsetRef.current && isItemsLoading) return [];
        return items;
    };
    const currentItems = autocompleteGetOptions();
    const renderedItems = 'load' === props.type ? allSelectedItems : currentItems;
    const autocompleteOptions = useMemo(()=>currentItems, [
        currentItems
    ]);
    const autocompleteGetInputValue = ()=>{
        if (loading || !isFirstRenderDone) return 'Загрузка...';
        if (!open && !inputValue) return selectedValues;
        return inputValue;
    };
    const autocompleteOpenHandler = ()=>{
        null == onLogAction || onLogAction('Открыть выпадающий список');
        setOpen(true);
    };
    const close = ()=>{
        setOpen(false);
        setSelectedValues(getSelectedLoadValues(renderedItems, value));
    };
    const autocompleteCloseHandler = ()=>{
        close();
    };
    const autocompleteBlurHandler = ()=>{
        if (open) close();
        null == onBlur || onBlur();
    };
    const autocompleteGetOptionLabel = (option)=>`${option.prefix ? `${option.prefix}, ` : ''}${option.label}`;
    const autocompleteGetOptionSelected = (option, selected)=>option.value === selected.value;
    const autocompleteGetOptionDisabled = (option)=>option.disabled || false;
    const autocompleteFocusHandler = ()=>{
        setOpen(true);
    };
    const autocompleteChangeHandler = (event, nextValue, reason)=>{
        event.stopPropagation();
        if ('local' === props.type) {
            onChange(nextValue.map((item)=>item.value));
            setAllSelectedItems(nextValue);
        } else {
            let valuesToSet = [];
            if ('selectOption' === reason) {
                valuesToSet = [
                    ...value || [],
                    ...nextValue.filter((item)=>!(null == value ? void 0 : value.includes(item.value))).map((item)=>item.value)
                ];
                setAllSelectedItems((prev)=>[
                        ...prev,
                        ...nextValue.filter((item)=>!prev.find(({ value: itemValue })=>itemValue === item.value))
                    ]);
            } else if ('removeOption' === reason) {
                const valuesToRemove = allSelectedItems.filter((item)=>(null == value ? void 0 : value.includes(item.value)) && !nextValue.find(({ value: itemValue })=>itemValue === item.value)).map((item)=>item.value);
                valuesToSet = [
                    ...(value || []).filter((item)=>!valuesToRemove.includes(item))
                ];
                setAllSelectedItems((prev)=>prev.filter((item)=>valuesToSet.includes(item.value)));
            }
            onChange(valuesToSet);
        }
    };
    const autocompleteClearHandler = (event)=>{
        event.stopPropagation();
        onChange([]);
        setAllSelectedItems([]);
    };
    const autocompleteInputChangeHandler = (event, nextValue, reason)=>{
        if ('reset' === reason || 'selectOption' === reason || 'removeOption' === reason) {
            if (!event || (null == event ? void 0 : event.target.closest('div#scrollable'))) return;
        } else setInputValue(nextValue);
        if ('clear' === reason) setSelectedValues('');
        if ('input' === reason && 'load' === props.type) {
            setIsLoading(true);
            offsetRef.current = 0;
            setFilter(_object_spread_props(_object_spread({}, filter), {
                offset: 0,
                query: nextValue
            }));
        }
    };
    const autocompleteSelectAllChangeHandler = ()=>{
        if ('load' === props.type) return;
        if (value && currentItems.filter((item)=>!item.disabled).length === value.length) {
            onChange([]);
            setSelectedValues('');
        } else {
            const itemsToSelect = currentItems.filter((item)=>!item.disabled).sort((a, b)=>a.label > b.label ? 1 : -1);
            onChange(itemsToSelect.map((item)=>item.value));
            setSelectedValues(itemsToSelect.map((item)=>`${item.prefix ? `${item.prefix}, ` : ''}${item.label}`).join(', '));
        }
    };
    useEffect(()=>{
        if ('local' === props.type) return;
        if (!open || '' !== inputValueForUse && inputValueForUse.length < minInputValueLength) return;
        offsetRef.current = 0;
        isInitialLoadRef.current = true;
        setFilter({
            limit,
            offset: 0,
            query: inputValueForUse
        });
        const abortRequest = null == requestItems ? void 0 : requestItems(_object_spread_props(_object_spread({}, additionalFilters), {
            offset: 0,
            limit,
            query: inputValueForUse
        }), 'search');
        setIsLoading(false);
        const timer = setTimeout(()=>{
            isInitialLoadRef.current = false;
        }, 500);
        return ()=>{
            null == abortRequest || abortRequest();
            clearTimeout(timer);
        };
    }, [
        open,
        inputValueForUse
    ]);
    useEffect(()=>{
        (()=>_async_to_generator(function*() {
                if ((null == value ? void 0 : value.length) && !open && 'load' === props.type && !selectedValues) {
                    var _props_loadItems;
                    const itemsByIds = yield null == (_props_loadItems = props.loadItems) ? void 0 : _props_loadItems.call(props, _object_spread({
                        limit: value.length,
                        offset: 0,
                        query: '',
                        ids: value
                    }, additionalFilters));
                    const sortedItems = null == itemsByIds ? void 0 : itemsByIds.sort((a, b)=>a.label > b.label ? 1 : -1);
                    if (sortedItems) setSelectedValues(getSelectedLoadValues(sortedItems, value));
                    setAllSelectedItems(sortedItems || []);
                }
                setIsFirstRenderDone(true);
            })())();
    }, [
        value,
        selectedValues
    ]);
    useEffect(()=>{
        setSelectedValues(getSelectedLoadValues('local' === props.type ? props.externalItems : allSelectedItems, value));
    }, [
        value
    ]);
    const defaultRenderOption = (optionProps, option)=>{
        const { badge, additionalText } = option;
        const isBottom = (null == badge ? void 0 : badge.position) === 'bottom';
        return /*#__PURE__*/ createElement("li", _object_spread_props(_object_spread({}, optionProps), {
            key: option.value
        }), /*#__PURE__*/ jsxs("div", {
            className: classes.listItem,
            onClick: (event)=>option.disabled && event.stopPropagation(),
            children: [
                /*#__PURE__*/ jsx(Box, {
                    flexShrink: 0,
                    children: /*#__PURE__*/ jsx(Checkbox, {
                        withoutMargin: true,
                        sizeVariant: "large",
                        checked: !!value && value.some((item)=>item === option.value)
                    })
                }),
                badge && /*#__PURE__*/ jsx(Box, {
                    mr: "8",
                    order: isBottom ? 1 : 0,
                    width: 64,
                    flexShrink: 0,
                    children: /*#__PURE__*/ jsx(Badge, {
                        title: badge.title,
                        width: "100%",
                        description: badge.description,
                        color: badge.color,
                        size: "extraSmall"
                    })
                }),
                option.disabled ? /*#__PURE__*/ jsx("div", {
                    className: classes.listItemLabel,
                    children: /*#__PURE__*/ jsx(Tooltip, {
                        title: `${disabledTooltip}${option.tooltipText ? ` ${option.tooltipText}` : ''}`,
                        children: /*#__PURE__*/ jsxs("div", {
                            children: [
                                option.prefix ? `${option.prefix}, ` : '',
                                option.label
                            ]
                        })
                    })
                }) : /*#__PURE__*/ jsxs(Box, {
                    display: "flex",
                    flexDirection: "column",
                    gap: "4",
                    maxWidth: "calc(100% - 42px)",
                    children: [
                        /*#__PURE__*/ jsxs("div", {
                            className: classes.defaultWordBreak,
                            children: [
                                option.prefix ? `${option.prefix}, ` : '',
                                option.label
                            ]
                        }),
                        additionalText && /*#__PURE__*/ jsx(Typography, {
                            variant: "caption",
                            color: "text.light.tertirary",
                            children: option.additionalText
                        })
                    ]
                })
            ]
        }));
    };
    const customListboxProps = {
        dataLength: currentItems.length,
        next: ()=>{
            if (isInitialLoadRef.current) return;
            const currentOffset = offsetRef.current;
            const nextOffset = currentOffset + limit;
            offsetRef.current = nextOffset;
            null == requestItems || requestItems(_object_spread_props(_object_spread({}, additionalFilters), {
                offset: nextOffset,
                limit,
                query: filter.query
            }), 'get');
            setFilter((prevFilter)=>_object_spread_props(_object_spread({}, prevFilter), {
                    offset: nextOffset
                }));
        },
        hasMore: 'load' === props.type ? hasMore : false,
        loading: isItemsLoading
    };
    return /*#__PURE__*/ jsxs(Box, {
        className: classnames(classes.multipleAutocompleteInput, !withoutMargin && classes.defaultMargin),
        children: [
            label && /*#__PURE__*/ jsx(Label, {
                label: label,
                required: required,
                tooltip: tooltip,
                icon: labelIcon,
                inputId: inputId
            }),
            /*#__PURE__*/ jsx(Autocomplete, _object_spread_props(_object_spread({
                disableClearable: true,
                "data-testid": testId,
                filterOptions: 'load' === props.type ? (options)=>options : void 0,
                open: open,
                id: inputId,
                onOpen: autocompleteOpenHandler,
                onClose: autocompleteCloseHandler,
                onFocus: autocompleteFocusHandler,
                getOptionLabel: autocompleteGetOptionLabel,
                onChange: autocompleteChangeHandler,
                onBlur: autocompleteBlurHandler,
                classes: {
                    paper: classes.paper,
                    option: classes.option,
                    listbox: classes.listbox
                },
                disabled: disabled || loading,
                multiple: true,
                disableCloseOnSelect: true,
                clearOnEscape: true,
                popupIcon: /*#__PURE__*/ jsx(Box, {
                    m: "4",
                    children: /*#__PURE__*/ jsx(Icon, {
                        name: "arrow_triangle_down",
                        size: "16",
                        color: "text.dark.secondary"
                    })
                }),
                options: autocompleteOptions,
                noOptionsText: "Ничего не найдено...",
                value: renderedItems.filter((item)=>null == value ? void 0 : value.includes(item.value)),
                defaultValue: [],
                inputValue: autocompleteGetInputValue(),
                onInputChange: autocompleteInputChangeHandler,
                loading: isItemsLoading,
                loadingText: /*#__PURE__*/ jsx(Box, {
                    display: "flex",
                    justifyContent: "center",
                    p: "16",
                    children: /*#__PURE__*/ jsx(CircularProgress, {
                        size: 30
                    })
                }),
                renderOption: renderOption ? (optionProps, option)=>/*#__PURE__*/ createElement("li", _object_spread_props(_object_spread({}, optionProps), {
                        key: option.value
                    }), renderOption(option, value)) : defaultRenderOption
            }, isThereSomeGroup ? {
                groupBy: ({ group })=>{
                    if (group) return group;
                    return 'Заявки без группировки по коллекциям';
                }
            } : {}), {
                renderGroup: (params)=>/*#__PURE__*/ jsxs(Fragment, {
                        children: [
                            /*#__PURE__*/ jsx(Box, {
                                px: "16",
                                pt: "8",
                                pb: "4",
                                children: /*#__PURE__*/ jsx(Typography, {
                                    component: "span",
                                    variant: "caption",
                                    color: "text.light.tertirary",
                                    children: params.group
                                })
                            }),
                            /*#__PURE__*/ jsx(Box, {
                                children: params.children
                            })
                        ]
                    }),
                isOptionEqualToValue: autocompleteGetOptionSelected,
                getOptionDisabled: autocompleteGetOptionDisabled,
                slotProps: {
                    listbox: _object_spread({
                        component: ListBox
                    }, customListboxProps)
                },
                renderInput: (params)=>{
                    var _params_InputProps, _params_InputProps1, _params_InputProps2;
                    return /*#__PURE__*/ jsx(TextField, _object_spread_props(_object_spread({}, params), {
                        name: name,
                        classes: {
                            root: classes.input
                        },
                        variant: "outlined",
                        autoComplete: "off",
                        fullWidth: true,
                        disabled: disabled || loading || !isFirstRenderDone,
                        error: error,
                        helperText: helperText,
                        placeholder: placeholder || label,
                        slotProps: {
                            input: {
                                className: null == params ? void 0 : null == (_params_InputProps = params.InputProps) ? void 0 : _params_InputProps.className,
                                ref: null == params ? void 0 : null == (_params_InputProps1 = params.InputProps) ? void 0 : _params_InputProps1.ref,
                                endAdornment: loading || !isFirstRenderDone ? /*#__PURE__*/ jsx(Preloader, {}) : /*#__PURE__*/ jsxs(Box, {
                                    display: "flex",
                                    alignItems: "center",
                                    mx: "16",
                                    gap: "4",
                                    children: [
                                        clearable && !disabled && !!(null == value ? void 0 : value.length) && !!selectedValues && /*#__PURE__*/ jsx(Box, {
                                            width: 24,
                                            height: 24,
                                            children: /*#__PURE__*/ jsx(Box, {
                                                className: `${classes.clearIcon} multipleAutocompleteClearIcon`,
                                                onClick: autocompleteClearHandler,
                                                children: /*#__PURE__*/ jsx(Icon, {
                                                    name: "dismiss_substract",
                                                    color: "stroke.secondary",
                                                    size: "24"
                                                })
                                            })
                                        }),
                                        /*#__PURE__*/ jsx(Box, {
                                            children: null == params ? void 0 : null == (_params_InputProps2 = params.InputProps) ? void 0 : _params_InputProps2.endAdornment
                                        })
                                    ]
                                })
                            }
                        }
                    }));
                }
            })),
            'local' === props.type && props.allowSelectAll && /*#__PURE__*/ jsx(FormControlLabel, {
                control: /*#__PURE__*/ jsx(Checkbox, {
                    withoutMargin: true,
                    sizeVariant: "large",
                    disabled: disabled,
                    checked: !!(null == value ? void 0 : value.length) && currentItems.filter((item)=>!item.disabled).length === value.length,
                    onChange: autocompleteSelectAllChangeHandler
                }),
                label: /*#__PURE__*/ jsx("span", {
                    className: classes.checkboxLabel,
                    children: props.selectAllLabel || 'Выбрать всех'
                })
            }),
            renderTags && !disabled && !!(null == value ? void 0 : value.length) && /*#__PURE__*/ jsx(Box, {
                mt: "16",
                children: renderTags(value, 'load' === props.type ? allSelectedItems : props.externalItems)
            })
        ]
    });
};
export { MultipleAutocompleteInput };
